import { memo, useEffect, useState, useMemo, useCallback, useRef, MouseEvent as ReactMouseEvent } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Page_Size, showBigImage, hideImage } from '../../../../Components/helper';
import { filterSortData, getSampleRequestsTodoList, saveBulkSamplePriority, setSampleSelectedIds } from './store/TodoList.action';
import SampleTrackingDatesModal from './ModalPopups/SampleTrackingDatesModal';
import { updateSamplePriority, submitEditSampleForm, getSampleTraking } from '../../../DevlopmentOrder/store/DevelopmentOrder.action';
import '../Todo.css';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { getCatBasedOnPT, getSubBrandAccess } from '../../../SystemMasters/Store/SystemMasters.action';
import AGGridTable from '../../../../Components/AG-GridTable/AGGridTable';
import type { BombiixColDef } from '../../../../Components/AG-GridTable/AGGridTable';
import Loader from '../../../../Components/Utility/Loader/Loader';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import SampleApprovalStatusTag from '../../../../Components/SampleComment/SampleApprovalStatusTag';
import {
  SamplesRootState,
  ListProps,
  PrioritySelectProps,
  ProductBrand,
  SampleGridRow,
  GridCellParams,
  SampleStatusRaw,
  GridApi,
  GridEvent,
  SampleTrackingFormData,
  SampleSourceRow,
} from './store/Samples.type';
import type { CellValueChangedEvent, ProcessCellForExportParams, SuppressKeyboardEventParams } from '@ag-grid-community/core';
import { copyFillValue } from '../../../../Components/BombiixAGGrid/fillClipboard';
import { canFillPriority, isPriorityCellEditingKey, planPriorityWrites, PRIORITY_WRITE_SOURCES, resolvePriorityText } from './priority-fill';
import type { PriorityFillGates, PriorityWrite } from './priority-fill';

const PRIORITY_CONFIG: Record<string, { label: string; icon: string }> = {
  highest: { label: 'Highest', icon: 'icon-highest' },
  high: { label: 'High', icon: 'icon-high' },
  medium: { label: 'Medium', icon: 'icon-Vector' },
  low: { label: 'Low', icon: 'icon-low' },
  lowest: { label: 'Lowest', icon: 'icon-lowest' },
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  green: 'sample-status-badge--green',
  red: 'sample-status-badge--red',
  amber: 'sample-status-badge--amber',
  grey: 'sample-status-badge--grey',
};

const PRIORITY_ORDER = ['highest', 'high', 'medium', 'low', 'lowest'];
const CELL_TOOLTIP_CLASS = 'custom-tooltip samples-cell-tooltip';

function PrioritySelect({ value, disabled, onChange, captureMode = false, captured = false }: PrioritySelectProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const key = value ? String(value).toLowerCase() : null;
  const priorityMeta = key ? PRIORITY_CONFIG[key] : null;
  const priorityClass = key ? `priority--${key}` : '';

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const toggle = (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    if (captureMode) {
      onChange?.(value ?? null);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    setOpen((v) => !v);
  };

  const pick = (val: string | null) => {
    setOpen(false);
    if (val !== value) onChange?.(val);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        className={`priority-select__trigger ${priorityClass}${captured ? ' is-captured' : ''}`}
      >
        <span className="priority-select__label">
          {priorityMeta ? priorityMeta.label : 'Priority'}
          {priorityMeta ? (
            <i className={`priority-select__icon ${priorityMeta.icon}`} aria-hidden="true" />
          ) : (
            <i className="priority-select__icon priority-select__icon--empty icon-low" aria-hidden="true" />
          )}
        </span>
      </button>
      {open &&
        ReactDOM.createPortal(
          <div ref={menuRef} className="priority-select__menu" style={{ top: pos.top, left: pos.left, minWidth: Math.max(pos.width, 110) }}>
            {PRIORITY_ORDER.map((pk) => {
              const opt = PRIORITY_CONFIG[pk];
              if (!opt) return null;
              const active = pk === key;
              return (
                <button
                  key={pk}
                  type="button"
                  onClick={() => pick(opt.label)}
                  className={`priority-select__option priority--${pk}${active ? ' is-active' : ''}`}
                >
                  {opt.label}
                  <i className={`priority-select__icon ${opt.icon}`} aria-hidden="true" />
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

function joinDistinct(arr: unknown) {
  if (!Array.isArray(arr) || arr.length === 0) return '—';
  const values = [...new Set(arr.filter((value) => value !== null && value !== undefined && value !== ''))];
  return values.length ? values.join(', ') : '—';
}

function sampleTrackingStatusText(data: SampleGridRow | undefined, dateFormat: string) {
  const status = data?.sampleTrackingStatus;
  const label = (data?.sampleTrackingStatusArr as { label?: string } | undefined)?.label;
  const prefix = label ? `Sample ${label}, ${data?.modifiedBy ?? ''}, ` : '';
  const dateText = status ? moment(status).format(dateFormat) : '—';
  return `${prefix}${dateText}`;
}

function StatusBadge({ raw }: { raw?: SampleStatusRaw | SampleStatusRaw[] | undefined }) {
  const single = Array.isArray(raw) ? raw[0] : raw;
  const name = single?.name;
  const color = single?.statusColor;
  if (!name) return <span className="sample-status-empty">—</span>;
  const badgeClass = (color ? STATUS_BADGE_CLASS[color] : undefined) ?? STATUS_BADGE_CLASS.amber;
  return (
    <CustomTooltip tooltipText={name} tooltipClass={CELL_TOOLTIP_CLASS}>
      <span className={`sample-status-badge ${badgeClass}`}>
        <span className="sample-status-badge__text">{name}</span>
      </span>
    </CustomTooltip>
  );
}

function ActionIconsCell({
  data,
  onTracking,
  canEdit,
  t,
}: {
  data?: SampleGridRow | undefined;
  onTracking: (item: SampleGridRow) => void;
  canEdit?: boolean | undefined;
  t: (key: string) => string;
}) {
  if (!data) return null;
  const created = data.createdAt ? moment(data.createdAt).format('DD-MMM-YYYY hh:mm A') : '';
  const modified = data.updatedAt ? moment(data.updatedAt).format('DD-MMM-YYYY hh:mm A') : '';
  const infoContent = (
    <>
      Created: {data.createdBy || ''} {created}
      <br />
      Modified: {data.modifiedBy || ''} {modified}
    </>
  );
  return (
    <div className="d-flex align-items-center sample-action-icons">
      {canEdit && (
        <CustomTooltip tooltipText={t('TAKING_DATES.EDIT_SAMPLE_TRACKING')} tooltipClass={CELL_TOOLTIP_CLASS}>
          <button type="button" onClick={() => onTracking(data)} className="btn btn-action-icon mx-0 p-0 sample-action-icons__btn">
            <span className="icon-location" />
          </button>
        </CustomTooltip>
      )}
      <CustomTooltip tooltipText={infoContent} tooltipClass={CELL_TOOLTIP_CLASS}>
        <span className="icon-details sample-action-icons__info" />
      </CustomTooltip>
    </div>
  );
}

/**
 * Product thumbnail cell. Memoised on `data` so a mounted cell never re-renders while its
 * row object is unchanged (AG Grid refreshes cells on selection/model updates, which would
 * otherwise recreate the <img>). `loading` is lazy: the `src` is the full-size original
 * upload (can be 10MB+), so eager loading would download and decode every buffered off-screen
 * row on each scroll — a burst that stalls scrolling. Lazy defers each fetch until the cell
 * nears the viewport. The durable fix is a server-side thumbnail; until then this bounds the cost.
 */
const ImageCell = memo(function ImageCell({ data }: { data?: SampleGridRow | undefined }) {
  return (
    <div className="image-detail hover-right sample-image-cell">
      <figure onMouseOver={(e) => showBigImage(e)} onMouseLeave={hideImage} className="sample-image-cell__figure">
        {data?.image ? (
          <img
            alt="product"
            src={data?.image}
            data-full-src={typeof data?.imageFull === 'string' && data.imageFull ? data.imageFull : undefined}
            className="sample-image-cell__img"
            decoding="async"
            loading="lazy"
            width={32}
            height={32}
          />
        ) : (
          <span className="sample-image-cell__placeholder">
            <i className="icon-group f-20" />
          </span>
        )}
      </figure>
    </div>
  );
});

function SampleRefCell({ data }: { data?: SampleGridRow | undefined }) {
  if (!data) return <span>—</span>;
  if (!data.devOrderId) return <span className="sample-ref-text">{data.sampleNumber || '—'}</span>;
  return (
    <Link
      className="sample-ref-link"
      to={`/development-order/${data.devOrderId}/samples-details/comments?sample=${data._id}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {data.sampleNumber || '—'}
    </Link>
  );
}

/**
 * Single-line, ellipsised grid text. The full value is exposed through the native
 * `title` attribute so it is available on hover without the per-cell overhead of a
 * React overlay component — critical when ~20 text columns render across every
 * virtualised row. Empty values render a plain em-dash with no title.
 */
function TooltipCell({ value }: { value?: unknown }) {
  const hasValue = value !== null && value !== undefined && value !== '' && value !== '—';
  const text = hasValue ? String(value) : '—';
  const label = <span className="sample-cell-text">{text}</span>;
  if (!hasValue) return label;
  return (
    <CustomTooltip tooltipText={text} tooltipClass={CELL_TOOLTIP_CLASS}>
      {label}
    </CustomTooltip>
  );
}

/**
 * Fallback renderer that also *tags* a column as plain text. Columns declared with
 * it are rewritten by `normalizeTextColumns` into native AG Grid text cells (no React
 * component) so they paint synchronously with the row and never blank during scroll.
 */
const renderTextCell = ({ value }: GridCellParams) => <TooltipCell value={value} />;

const DASH = '—';

/**
 * Value formatter for plain-text columns: coerces empty cell values to an em-dash so
 * blanks read consistently; real values pass through untouched.
 */
const formatCellText = ({ value }: GridCellParams) => (value === null || value === undefined || value === '' ? DASH : String(value));

/**
 * Browser-tooltip text for a plain-text column: the formatted value, or an empty
 * string when the cell is blank so no tooltip shows on an em-dash.
 */
const cellTextTooltip = ({ value }: GridCellParams) => {
  const text = formatCellText({ value } as GridCellParams);
  return text === DASH ? '' : text;
};

/**
 * Custom AG Grid tooltip for text cells — reproduces the app's dark tooltip look.
 * AG Grid mounts a single instance on cell hover (not one per cell), so the styled
 * tooltip is restored without the per-cell React cost that blanks rows while scrolling.
 */
function GridTextTooltip({ value }: { value?: unknown }) {
  if (value === null || value === undefined || value === '') return null;
  return <div className="samples-grid-tooltip">{String(value)}</div>;
}

/**
 * Rewrites every column tagged with `renderTextCell` into a native text cell: drops
 * the React `cellRenderer`, formats the value directly and exposes the full value
 * through a browser tooltip. Native cells are painted by AG Grid synchronously, which
 * is what stops rows rendering blank while scrolling across many columns.
 */
function normalizeTextColumns(cols: Array<Record<string, unknown>>) {
  return cols.map((col) => {
    if (col.cellRenderer !== renderTextCell) return col;
    const existingClass = typeof col.cellClass === 'string' ? `${col.cellClass} ` : '';
    return {
      ...col,
      cellRenderer: undefined,
      valueFormatter: formatCellText,
      tooltipValueGetter: cellTextTooltip,
      cellClass: `${existingClass}sample-text-cell`,
    };
  });
}

/** Sample Ref cell: the product-order link, with the sample number shown on hover. */
const renderSampleRefCell = ({ data }: GridCellParams) => {
  const label = data?.sampleNumber ? String(data.sampleNumber) : '—';
  if (!data?.sampleNumber) return <SampleRefCell data={data} />;
  return (
    <CustomTooltip tooltipText={label} tooltipClass={CELL_TOOLTIP_CLASS}>
      <span className="sample-cell-text">
        <SampleRefCell data={data} />
      </span>
    </CustomTooltip>
  );
};

/**
 * Flattens a raw sample's colourway / grading-group data into the distinct-value
 * arrays the grid columns render. Pure and side-effect free, so a caller can safely
 * memoise the result per row and reuse it while the source object is unchanged.
 */
function buildSampleRow(sample: SampleSourceRow) {
  const totColours: unknown[] = [];
  const totSize: unknown[] = [];
  const totLength: unknown[] = [];
  const totQuantity: unknown[] = [];
  const totGradingGroups: unknown[] = [];
  const totUom: unknown[] = [];

  const hasGradingRows = Array.isArray(sample.gradingGroupData) && sample.gradingGroupData.length > 0;
  const hasColourwayGroup = Array.isArray(sample.colourwayGroupData) && sample.colourwayGroupData.length > 0;
  const useLegacy = !hasGradingRows && !hasColourwayGroup;

  if (useLegacy) {
    (sample.colourWays ?? []).forEach((cw) => {
      totColours.push(cw?.colourway?.name);
      totSize.push(cw?.size);
      totLength.push(cw?.len?.name);
      totQuantity.push(cw?.quantity);
    });
  } else {
    (sample.colourwayGroupData ?? []).forEach((cw) => totColours.push(cw?.name));
    (sample.gradingGroupData ?? []).forEach((ggd) => {
      totSize.push(ggd?.size);
      totLength.push(ggd?.len?.name);
      totQuantity.push(ggd?.quantity);
      totGradingGroups.push(ggd?.gradingGroup?.name);
      totUom.push(ggd?.uom?.name);
    });
  }

  return {
    ...sample,
    totColours: [...new Set(totColours)],
    totSize: [...new Set(totSize)],
    totLength: [...new Set(totLength)],
    totQuantity: [...new Set(totQuantity)],
    totGradingGroups: [...new Set(totGradingGroups)],
    totUom: [...new Set(totUom)],
  };
}

type BuiltSampleRow = ReturnType<typeof buildSampleRow>;

function List({
  loading,
  getSelectedCount,
  lengthEnabled,
  bulkPriorityMode = false,
  onCapturePriority,
  capturedRowId = null,
  clearSelectionToken = 0,
}: ListProps) {
  const {
    sampleTodoList,
    sampleTodoListTotalCount,
    filterValues: {
      searchText,
      pageIndex,
      sortOrder,
      sortPropertyName,
      selectedSeason,
      selectedYear,
      selectedBrand,
      selectedSubBrand,
      selectedSupplier,
      selectedSampleType,
      selectedColour,
      selectedColourGroup,
      selectedProductType,
      selectedTrackingStatus,
      selectedSampleStatus,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      selectedCategory4,
    },
  } = useSelector((s: SamplesRootState) => s.SampleTodoListData);
  const { editDataForSampleTrackingDates, sampleTrakingDates } = useSelector((s: SamplesRootState) => s.Measuremets);
  const userInfo = useSelector((s: SamplesRootState) => s.UserDetails?.userInfo);
  const { userId } = userInfo ?? {};
  const isSupplierLoggedIn = useSelector((s: SamplesRootState) => Boolean(s.UserDetails?.isSupplierLoggedIn));
  const { logInRights } = useSelector((s: SamplesRootState) => s.Users);
  const { subBrandAccess } = useSelector((s: SamplesRootState) => s.SystemMastersDetails);

  const dispatch = useDispatch();
  const dateFormat = 'DD-MMM-YYYY';
  const { t } = useTranslation('common');
  const { CheckActionPermission } = useComponent();

  const [selectedIdsArray, setSelectedIdsArray] = useState<string[]>([]);
  const [selectedData, setSelectedData] = useState<SampleGridRow[]>([]);
  const [userRights, setUserRights] = useState<Record<string, unknown>>({});
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [openTrackingModal, setOpenTrackingModal] = useState(false);
  const [sampleEditData, setSampleEditData] = useState<SampleGridRow | null>(null);
  const [firstDataPainted, setFirstDataPainted] = useState(false);
  const [isSorting, setIsSorting] = useState(false);

  const sampleTrakingDatesRef = useRef(sampleTrakingDates);
  const lengthEnabledRef = useRef(lengthEnabled);
  const filterRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    sampleTrakingDatesRef.current = sampleTrakingDates;
  }, [sampleTrakingDates]);
  useEffect(() => {
    lengthEnabledRef.current = lengthEnabled;
  }, [lengthEnabled]);
  useEffect(() => {
    filterRef.current = {
      searchText,
      selectedSeason,
      selectedYear,
      selectedBrand,
      selectedSubBrand,
      selectedSupplier,
      selectedSampleType,
      selectedColour,
      selectedColourGroup,
      selectedProductType,
      selectedTrackingStatus,
      selectedSampleStatus,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      selectedCategory4,
    };
  });

  useEffect(() => {
    (dispatch(getCatBasedOnPT()) as unknown as Promise<unknown>).then((res: unknown) => setCatBasedOnPTData(!!res)).catch(() => setCatBasedOnPTData(false));
    dispatch(getSubBrandAccess());
  }, [dispatch]);

  useEffect(() => {
    if (logInRights) setUserRights(logInRights);
  }, [logInRights]);
  useEffect(() => {
    getSelectedCount(selectedData);
  }, [selectedData]);

  useEffect(() => {
    if (!clearSelectionToken) return;
    setSelectedIdsArray([]);
    setSelectedData([]);
  }, [clearSelectionToken]);

  const sampleAddEditAccess = (CheckActionPermission as (...args: unknown[]) => boolean)(userRights, 'products', 'samples', 'addEdit');
  /* Read through a ref by the Priority column's editable rule, so the column memo need not rebuild on
     every rights or mode change. */
  const priorityGatesRef = useRef<PriorityFillGates>({ isSupplierLoggedIn, sampleAddEditAccess, bulkPriorityMode });
  priorityGatesRef.current = { isSupplierLoggedIn, sampleAddEditAccess, bulkPriorityMode };

  const openTrakingModel = useCallback((item: SampleGridRow) => {
    setSampleEditData(item);
    setOpenTrackingModal(true);
  }, []);

  const handleSelectionChanged = useCallback(
    (rows: SampleGridRow[]) => {
      const ids = rows.map((r) => r._id) as string[];
      setSelectedIdsArray(ids);
      setSelectedData(rows);
      dispatch(setSampleSelectedIds(ids));
    },
    [dispatch],
  );

  const handleServerSort = useCallback(
    (colId: string, direction: string) => {
      const sortOrderVal = direction;
      const sortColId = direction ? colId : '';
      const currentFilter = filterRef.current;
      const sortObj = {
        sortOrder: sortOrderVal,
        sortPropertyName: sortColId,
        searchText: currentFilter.searchText,
        pageIndex: 1,
        pageSize: Page_Size,
        type: 'samples',
        seasons: currentFilter.selectedSeason,
        year: currentFilter.selectedYear,
        brand: currentFilter.selectedBrand,
        subBrand: currentFilter.selectedSubBrand || [],
        supplier: currentFilter.selectedSupplier,
        sampleType: currentFilter.selectedSampleType,
        colour: currentFilter.selectedColour,
        colorGroupId: currentFilter.selectedColourGroup || [],
        productTypeId: Array.isArray(currentFilter.selectedProductType) ? currentFilter.selectedProductType : [],
        trackingStatus: currentFilter.selectedTrackingStatus,
        status: currentFilter.selectedSampleStatus,
        category1: currentFilter.selectedCategory1,
        category2: currentFilter.selectedCategory2,
        category3: currentFilter.selectedCategory3,
        category4: currentFilter.selectedCategory4,
      };
      dispatch(filterSortData({ sortOrder: sortOrderVal, sortPropertyName: sortColId, pageIndex: 1, pageSize: Page_Size }));
      setIsSorting(true);
      (dispatch(getSampleRequestsTodoList(sortObj)) as unknown as Promise<unknown>).finally(() => setIsSorting(false));
    },
    [dispatch],
  );

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const sampleTodoListCountRef = useRef(0);
  const sampleTotalCountRef = useRef(0);
  const pageIndexRef = useRef(1);

  useEffect(() => {
    sampleTodoListCountRef.current = sampleTodoList?.length ?? 0;
  }, [sampleTodoList]);
  useEffect(() => {
    sampleTotalCountRef.current = sampleTodoListTotalCount ?? 0;
  }, [sampleTodoListTotalCount]);
  useEffect(() => {
    pageIndexRef.current = pageIndex ?? 1;
  }, [pageIndex]);

  const loadNextPage = useCallback(() => {
    if (isLoadingMoreRef.current) return;
    if (sampleTodoListCountRef.current >= sampleTotalCountRef.current) return;
    const currentFilter = filterRef.current;
    const nextPage = pageIndexRef.current + 1;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    dispatch(filterSortData({ pageIndex: nextPage, pageSize: Page_Size }));
    (
      dispatch(
        getSampleRequestsTodoList({
          pageIndex: nextPage,
          pageSize: Page_Size,
          isScrolled: true,
          totalRecords: sampleTotalCountRef.current,
          type: 'samples',
          searchText: currentFilter.searchText,
          seasons: currentFilter.selectedSeason,
          year: currentFilter.selectedYear,
          brand: currentFilter.selectedBrand,
          subBrand: currentFilter.selectedSubBrand || [],
          supplier: currentFilter.selectedSupplier,
          sampleType: currentFilter.selectedSampleType,
          colour: currentFilter.selectedColour,
          colorGroupId: currentFilter.selectedColourGroup || [],
          productTypeId: Array.isArray(currentFilter.selectedProductType) ? currentFilter.selectedProductType : [],
          trackingStatus: currentFilter.selectedTrackingStatus,
          status: currentFilter.selectedSampleStatus,
          category1: currentFilter.selectedCategory1,
          category2: currentFilter.selectedCategory2,
          category3: currentFilter.selectedCategory3,
          category4: currentFilter.selectedCategory4,
          sortOrder: sortOrder || '',
          sortPropertyName: sortPropertyName || '',
        }),
      ) as unknown as Promise<unknown>
    ).finally(() => {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    });
  }, [dispatch, sortOrder, sortPropertyName]);

  const gridApiRef = useRef<GridApi | null>(null);
  const getScrollViewport = () =>
    document.querySelector('.samples-aggrid-wrapper .ag-body-vertical-scroll-viewport') || document.querySelector('.samples-aggrid-wrapper .ag-body-viewport');

  const maybeLoadMore = useCallback(() => {
    const vp = getScrollViewport();
    if (!vp) return;
    const { scrollTop, scrollHeight, clientHeight } = vp;
    const prefetchMargin = Math.max(clientHeight * 1.5, 120);
    if (scrollHeight - scrollTop - clientHeight <= prefetchMargin) loadNextPage();
  }, [loadNextPage]);

  const handleBodyScroll = useCallback(
    (e: GridEvent) => {
      if (e.direction !== 'vertical') return;
      maybeLoadMore();
    },
    [maybeLoadMore],
  );
  const handleFirstDataRendered = useCallback((e: GridEvent) => {
    gridApiRef.current = e.api;
    setFirstDataPainted(true);
  }, []);

  const handlePriorityChange = useCallback(
    async (row: SampleGridRow | undefined, priority: string | null) => {
      if (!row?.devOrderId || !row?._id) return;
      const translate = (msgKey: string) => t(msgKey);
      const res = (await dispatch(updateSamplePriority(row.devOrderId, row._id, priority, translate))) as { data?: { code?: number } } | undefined;
      if (res?.data?.code === 200) {
        const node = gridApiRef.current?.getRowNode?.(String(row._id));
        if (node) node.setDataValue('priority', priority ?? null);
      }
    },
    [dispatch, t],
  );

  const onPriorityPillChange = useCallback(
    (row: SampleGridRow | undefined, priority: string | null) => {
      if (bulkPriorityMode) {
        onCapturePriority?.(priority ?? null, row?._id ?? null);
        return;
      }
      handlePriorityChange(row, priority);
    },
    [bulkPriorityMode, onCapturePriority, handlePriorityChange],
  );

  const submitSampleTrackingForm = async (data: SampleTrackingFormData) => {
    const updatedData: Record<string, unknown> = { ...editDataForSampleTrackingDates };
    updatedData.devOrderId = sampleEditData?.devOrderId;
    updatedData.parentId = null;
    updatedData.sampleType = (updatedData.sampleType as { _id?: string } | undefined)?._id;
    updatedData.supplier = (updatedData.supplier as { _id?: string } | undefined)?._id;
    updatedData.sampleId = sampleEditData?._id;
    updatedData.loggedInUserId = userId;
    updatedData.trackingDates = (data.trackingDates ?? []).map((track) => ({
      label: `${track.name?.replace(/\s+/g, ' ').trim()} Date`,
      value: data.requestType === 'request' && track.refKey === 'requested' ? null : track?.value || null,
      trackingUrl: track?.trackingUrl || null,
      sampleId: track?.sampleId ? track.sampleId._id : track._id,
      key: track?.refKey || track?.key,
      _id: track._id,
    }));
    updatedData.colourwayArray = data?.colourWays;
    updatedData.dueDate = data?.dueDate;
    const statusCode = (await dispatch(submitEditSampleForm(updatedData, t))) as unknown as number | undefined;
    if (statusCode === 200) {
      setSampleEditData(null);
      setOpenTrackingModal(false);
      const currentFilter = filterRef.current;
      const sortObj = {
        searchText: currentFilter.searchText,
        pageIndex: 1,
        pageSize: Page_Size,
        type: 'samples',
        seasons: currentFilter.selectedSeason,
        year: currentFilter.selectedYear,
        brand: currentFilter.selectedBrand,
        subBrand: currentFilter.selectedSubBrand || [],
        supplier: currentFilter.selectedSupplier,
        sampleType: currentFilter.selectedSampleType,
        colour: currentFilter.selectedColour,
        colorGroupId: currentFilter.selectedColourGroup || [],
        productTypeId: Array.isArray(currentFilter.selectedProductType) ? currentFilter.selectedProductType : [],
        trackingStatus: currentFilter.selectedTrackingStatus,
        status: currentFilter.selectedSampleStatus,
        category1: currentFilter.selectedCategory1,
        category2: currentFilter.selectedCategory2,
        category3: currentFilter.selectedCategory3,
        category4: currentFilter.selectedCategory4,
      };
      await dispatch(getSampleTraking());
      await dispatch(getSampleRequestsTodoList(sortObj));
    }
  };

  const rowCacheRef = useRef(new Map<string, { src: SampleSourceRow; row: BuiltSampleRow }>());

  const rowData = useMemo(() => {
    if (!Array.isArray(sampleTodoList)) {
      rowCacheRef.current = new Map();
      return [];
    }
    const prevCache = rowCacheRef.current;
    const nextCache = new Map<string, { src: SampleSourceRow; row: BuiltSampleRow }>();
    const seenIds = new Set<string>();
    const rows: BuiltSampleRow[] = [];
    (sampleTodoList as SampleSourceRow[]).forEach((sample) => {
      const id = sample._id ? String(sample._id) : '';
      if (id) {
        if (seenIds.has(id)) return;
        seenIds.add(id);
      }
      const cached = id ? prevCache.get(id) : undefined;
      if (cached && cached.src === sample) {
        nextCache.set(id, cached);
        rows.push(cached.row);
        return;
      }
      const row = buildSampleRow(sample);
      if (id) nextCache.set(id, { src: sample, row });
      rows.push(row);
    });
    rowCacheRef.current = nextCache;
    return rows;
  }, [sampleTodoList]);

  const columnDefs = useMemo(
    () =>
      normalizeTextColumns([
        {
          headerName: 'Image',
          colId: '_image',
          field: '_image',
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          sortable: false,
          searchable: false,
          cellRenderer: ({ data }: GridCellParams) => <ImageCell data={data} />,
        },
        {
          headerName: t('TABLE_HEADERS_LIST.PRODUCT_REF'),
          colId: 'number',
          field: 'number',
          width: 180,
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('TABLE_HEADERS_LIST.PRODUCT_DESCRIPTION'),
          colId: 'description',
          field: 'description',
          width: 300,
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('TABLE_HEADERS_LIST.SAMPLE_REF'),
          colId: 'sampleNumber',
          field: 'sampleNumber',
          width: 150,
          cellRenderer: renderSampleRefCell,
        },
        { headerName: t('TABLE_HEADERS_LIST.SAMPLE_TYPE'), colId: 'sampleType', field: 'sampleType', width: 160, sortable: true, cellRenderer: renderTextCell },
        {
          headerName: t('samples:TABLE_HEADERS_LIST.SAMPLE_TYPE_GROUP'),
          colId: 'sampleTypeGroupId',
          field: 'sampleTypeGroupId',
          width: 160,
          sortable: true,
          cellRenderer: renderTextCell,
        },
        { headerName: t('TABLE_HEADERS_LIST.BRAND'), colId: 'brand', field: 'brandId', width: 130, cellRenderer: renderTextCell },
        ...(subBrandAccess
          ? [{ headerName: t('TABLE_HEADERS_LIST.SUB_BRAND'), colId: 'subBrand', field: 'subBrandId', width: 130, cellRenderer: renderTextCell }]
          : []),
        { headerName: t('TABLE_HEADERS_LIST.SUPPLIER'), colId: 'supplier', field: 'supplier', width: 150, sortable: true, cellRenderer: renderTextCell },
        {
          headerName: t('TABLE_HEADERS_LIST.SEASON'),
          colId: 'seasonId',
          field: 'seasonId',
          width: 150,
          cellRenderer: renderTextCell,
        },
        { headerName: t('TABLE_HEADERS_LIST.MONTH'), colId: 'month', field: 'month', width: 110, cellRenderer: renderTextCell },
        { headerName: t('TABLE_HEADERS_LIST.YEAR'), colId: 'year', field: 'year', width: 90, cellRenderer: renderTextCell },
        ...(catBasedOnPTData
          ? [
              {
                headerName: t('TABLE_HEADERS_LIST.PRODUCT_TYPE'),
                colId: 'productType',
                field: 'supplierType',
                width: 150,
                cellRenderer: renderTextCell,
              },
            ]
          : []),
        {
          headerName: t('TABLE_HEADERS_LIST.CATEGORY_1'),
          colId: 'catOneId',
          field: 'catOneId',
          width: 150,
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('TABLE_HEADERS_LIST.CATEGORY_2'),
          colId: 'catTwoId',
          field: 'catTwoId',
          width: 150,
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('TABLE_HEADERS_LIST.CATEGORY_3'),
          colId: 'catThreeId',
          field: 'catThreeId',
          width: 150,
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('TABLE_HEADERS_LIST.CATEGORY_4'),
          colId: 'catFourId',
          field: 'catFourId',
          width: 150,
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('COSTING_TO_DO_LIST.COLOR'),
          colId: '_color',
          width: 150,
          sortable: true,
          searchable: false,
          valueGetter: ({ data }: GridCellParams) => joinDistinct(data?.totColours),
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('samples:TABLE_HEADERS_LIST.GRADING_GROUP'),
          colId: '_gradingGroup',
          width: 150,
          sortable: true,
          searchable: false,
          valueGetter: ({ data }: GridCellParams) => joinDistinct(data?.totGradingGroups),
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('ORDER_PREVIEW_DETAILS.SIGE'),
          colId: '_size',
          width: 150,
          sortable: true,
          searchable: false,
          valueGetter: ({ data }: GridCellParams) => joinDistinct(data?.totSize),
          cellRenderer: renderTextCell,
        },
        ...(lengthEnabled
          ? [
              {
                headerName: t('PROGRESS_INDICATOR.LENGTH'),
                colId: '_length',
                width: 150,
                sortable: true,
                searchable: false,
                valueGetter: ({ data }: GridCellParams) => joinDistinct(data?.totLength),
                cellRenderer: renderTextCell,
              },
            ]
          : []),
        {
          headerName: t('ORDER_PREVIEW_DETAILS.QUANTITY'),
          colId: '_quantity',
          width: 150,
          sortable: true,
          searchable: false,
          valueGetter: ({ data }: GridCellParams) => joinDistinct(data?.totQuantity),
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('ORDER_PREVIEW_DETAILS.UOM'),
          colId: '_uom',
          width: 150,
          sortable: true,
          searchable: false,
          valueGetter: ({ data }: GridCellParams) => joinDistinct(data?.totUom),
          cellRenderer: renderTextCell,
        },
        {
          headerName: t('TABLE_HEADERS_LIST.DUE_DATES'),
          colId: 'dueDate',
          width: 150,
          valueGetter: ({ data }: GridCellParams) => (data?.dueDate ? moment(data.dueDate).format(dateFormat) : '—'),
          cellRenderer: renderTextCell,
        },
        ...(sampleTrakingDates ?? [])
          .filter((el) => el.isActive === true)
          .map((el) => ({
            headerName: el?.name,
            colId: `track_${el?.refKey ?? el?._id}`,
            width: 150,
            sortable: true,
            searchable: false,
            valueGetter: ({ data }: GridCellParams) => {
              const found = data?.trackingDates?.find((d) => d?.key === el?.refKey);
              return found?.value ? moment(found.value).format(dateFormat) : '—';
            },
            cellRenderer: renderTextCell,
          })),
        {
          headerName: t('TABLE_HEADERS_LIST.SAMPLE_TRACKING_STATUS'),
          colId: 'sampleTrackingStatus',
          width: 220,
          valueGetter: ({ data }: GridCellParams) => sampleTrackingStatusText(data, dateFormat),
          cellRenderer: renderTextCell,
        },
        {
          headerName: 'Priority',
          colId: 'priority',
          field: 'priority',
          width: 140,
          sortable: true,
          searchable: false,
          pinned: 'right',
          /* Editable for the fill handle and paste only; the pill stays the control a click uses. */
          editable: ({ data }: GridCellParams) => canFillPriority(data, priorityGatesRef.current),
          suppressKeyboardEvent: (params: SuppressKeyboardEventParams<SampleGridRow>) => isPriorityCellEditingKey(params.event),
          cellRenderer: ({ data }: GridCellParams) => (
            <PrioritySelect
              value={data?.priority as string}
              disabled={isSupplierLoggedIn || !sampleAddEditAccess || (!bulkPriorityMode && !data?.devOrderId)}
              captureMode={bulkPriorityMode}
              captured={bulkPriorityMode && capturedRowId != null && data?._id === capturedRowId}
              onChange={(val) => onPriorityPillChange(data, val)}
            />
          ),
        },
        {
          headerName: t(`DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.COMMENT_COLUMNS.${isSupplierLoggedIn ? 'STATUS_LABEL' : 'PRIMARY_STATUS_LABEL'}`, { ns: 'product-sample' }),
          colId: 'approveStatus',
          field: 'sampleStatus',
          width: 190,
          minWidth: 190,
          sortable: true,
          searchable: false,
          pinned: 'right',
          cellRenderer: ({ data }: GridCellParams) => (
            <div className="samples-status-cell">
              <StatusBadge raw={data?.sampleStatus} />
            </div>
          ),
        },
        ...(!isSupplierLoggedIn
          ? [
              {
                headerName: t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.COMMENT_COLUMNS.LICENSING_STATUS_LABEL', { ns: 'product-sample' }),
                colId: 'licensingStatus',
                field: 'licensingStatus',
                width: 190,
                minWidth: 190,
                sortable: true,
                searchable: false,
                cellRenderer: ({ data }: GridCellParams) =>
                  Boolean(data?.isLicensedProduct) ? (
                    <div className="samples-status-cell">
                      <SampleApprovalStatusTag value={(data?.licensingStatus as string) ?? null} />
                    </div>
                  ) : (
                    <span className="sample-status-empty">—</span>
                  ),
              },
              {
                headerName: t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.COMMENT_COLUMNS.CUSTOMER_STATUS_LABEL', { ns: 'product-sample' }),
                colId: 'customerStatus',
                field: 'customerStatus',
                width: 190,
                minWidth: 190,
                sortable: true,
                searchable: false,
                cellRenderer: ({ data }: GridCellParams) => (
                  <div className="samples-status-cell">
                    <SampleApprovalStatusTag value={(data?.customerStatus as string) ?? null} />
                  </div>
                ),
              },
            ]
          : []),
        {
          headerName: t('ORDER_PREVIEW_DETAILS.ACTION'),
          colId: '_action',
          field: '_action',
          width: 110,
          minWidth: 110,
          sortable: false,
          searchable: false,
          pinned: 'right',
          cellRenderer: ({ data }: GridCellParams) => <ActionIconsCell data={data} onTracking={openTrakingModel} canEdit={sampleAddEditAccess} t={t} />,
        },
      ]),
    [
      t,
      subBrandAccess,
      catBasedOnPTData,
      lengthEnabled,
      sampleTrakingDates,
      sampleAddEditAccess,
      openTrakingModel,
      onPriorityPillChange,
      bulkPriorityMode,
      capturedRowId,
      isSupplierLoggedIn,
    ],
  );

  const showLoader = Boolean(loading) || !firstDataPainted || isSorting;

  useEffect(() => {
    if (!loading && (rowData?.length ?? 0) === 0) setFirstDataPainted(true);
  }, [loading, rowData]);

  const pendingPriorityWritesRef = useRef<PriorityWrite[]>([]);

  /**
   * Saves the priorities a fill-handle drag or paste wrote. The cells of one drag arrive as separate
   * events, so they are collected for a tick and sent as one request per distinct priority through
   * the same bulk endpoint the bulk-priority action uses. A cell whose text is no priority, or whose
   * save fails, goes back to its previous value. The single-pill save and this restore write with
   * other event sources, so neither re-enters here. Declared before the grid options that read it.
   */
  const handlePriorityCellChange = useCallback(
    (event: CellValueChangedEvent<SampleGridRow>) => {
      if (event.colDef.field !== 'priority' || !PRIORITY_WRITE_SOURCES.has(event.source ?? '')) return;
      const sampleId = event.data?._id;
      if (!sampleId) return;
      const pending = pendingPriorityWritesRef.current;
      pending.push({ sampleId: String(sampleId), priority: (event.newValue as string | null) ?? null, previous: (event.oldValue as string | null) ?? null });
      if (pending.length > 1) return;
      const { api } = event;
      Promise.resolve().then(() => {
        const writes = pendingPriorityWritesRef.current.splice(0);
        const previousById = new Map(writes.map((write) => [write.sampleId, write.previous]));
        const restore = (sampleIds: string[]) => sampleIds.forEach((id) => api.getRowNode(id)?.setDataValue('priority', previousById.get(id) ?? null, 'restore'));
        const { groups, refused } = planPriorityWrites(writes);
        restore(refused.map((write) => write.sampleId));
        groups.forEach(({ priority, sampleIds }) => {
          Promise.resolve(dispatch(saveBulkSamplePriority(sampleIds, priority)))
            .then((result) => {
              if (!result) restore(sampleIds);
            })
            .catch(() => restore(sampleIds));
        });
      });
    },
    [dispatch],
  );

  const gridOptions = useMemo(
    () => ({
      rowHeight: 40,
      headerHeight: 40,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      animateRows: false,
      tooltipComponent: GridTextTooltip,
      tooltipShowDelay: 300,
      rowBuffer: 20,
      overlayLoadingTemplate:
        '<div class="custom_spinner"><div class="rect1"></div><div class="rect2"></div><div class="rect3"></div><div class="rect4"></div><div class="rect5"></div></div>',
      onBodyScroll: handleBodyScroll,
      onFirstDataRendered: handleFirstDataRendered,
      onGridReady: (params: GridEvent) => {
        gridApiRef.current = params.api;
        if (loading) params.api.showLoadingOverlay();
      },
      /* Fill handle and paste on the Priority column (BM-10184): a drag always copies, a paste must
         name a priority, and a click never opens AG Grid's editor over the pill. */
      suppressClickEdit: true,
      cellSelection: { handle: { mode: 'fill' as const, direction: 'y' as const, setFillValue: copyFillValue } },
      processCellFromClipboard: (params: ProcessCellForExportParams<SampleGridRow>) =>
        params.column.getColId() === 'priority' ? (resolvePriorityText(params.value) ?? params.node?.data?.priority ?? null) : params.value,
      onCellValueChanged: handlePriorityCellChange,
    }),
    [handleBodyScroll, handleFirstDataRendered, handlePriorityCellChange, loading],
  );

  return (
    <>
      <div className="samples-aggrid-wrapper">
        <AGGridTable
          rowData={rowData}
          columnDefs={columnDefs as unknown as BombiixColDef[]}
          enableClipboardContextMenu
          selectionEnabled
          suppressExpandColumn
          onSelectionChanged={handleSelectionChanged as (rows: unknown[]) => void}
          selectedRowIds={selectedIdsArray}
          rowIdField="_id"
          height="calc(100vh - 208px)"
          loading={showLoader}
          showColumnSearch={false}
          serverSideSort
          onServerSort={handleServerSort}
          enableColumnPinning
          persistKey="sample-todo"
          currentUserId={userId as string | undefined}
          gridOptions={gridOptions}
        />
        {isLoadingMore && (
          <div className="samples-load-more-bar">
            <Loader />
          </div>
        )}
      </div>
      {openTrackingModal && (
        <SampleTrackingDatesModal
          show={openTrackingModal}
          id={sampleEditData?._id as string}
          toggle={() => {
            dispatch(getSampleTraking());
            setOpenTrackingModal(false);
            setSampleEditData(null);
          }}
          onSubmitForm={(formData) => submitSampleTrackingForm(formData)}
          devId={sampleEditData?.devOrderId as string}
          productBrand={sampleEditData?.brand as ProductBrand[]}
        />
      )}
    </>
  );
}

/**
 * Memoised: with the parent's callback props now stable, List re-renders only on its own
 * Redux/state changes, not when the parent re-renders for unrelated local state (export or
 * bulk-status menus, etc.). Keeps AG Grid from reprocessing on parent-only updates.
 */
export default memo(List);
